<template>
  <div class="layout">
    <!-- 顶部导航 -->
    <header class="header" :class="{ scrolled: isScrolled }">
      <div class="header-inner">
        <div class="logo" @click="goHome">
          <div class="logo-icon">
            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="32" height="32" rx="8" fill="url(#logo-gradient)"/>
              <path d="M8 16L14 10L20 16L14 22Z" fill="white" opacity="0.9"/>
              <path d="M14 16L20 10L26 16L20 22Z" fill="white" opacity="0.6"/>
              <defs><linearGradient id="logo-gradient" x1="0" y1="0" x2="32" y2="32"><stop stop-color="#1890ff"/><stop offset="1" stop-color="#36cfc9"/></linearGradient></defs>
            </svg>
          </div>
          <span class="logo-text">科普征集</span>
        </div>
        <nav class="nav">
          <router-link to="/activity" class="nav-item" :class="{ active: $route.path.startsWith('/activity') }">
            <el-icon><Flag /></el-icon>
            <span>征集活动</span>
          </router-link>
          <router-link to="/gallery" class="nav-item" :class="{ active: $route.path.startsWith('/gallery') || $route.path.startsWith('/work') }">
            <el-icon><PictureFilled /></el-icon>
            <span>作品展示</span>
          </router-link>
        </nav>
        <div class="header-right">
          <template v-if="isLoggedIn">
            <el-dropdown trigger="click" @command="handleUserCommand">
              <div class="user-avatar-wrap">
                <div class="user-avatar">
                  {{ userName.charAt(0) }}
                </div>
                <span class="user-name">{{ userName }}</span>
                <el-icon class="arrow-down"><ArrowDown /></el-icon>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="center">
                    <el-icon><User /></el-icon>个人中心
                  </el-dropdown-item>
                  <el-dropdown-item command="works">
                    <el-icon><Document /></el-icon>我的作品
                  </el-dropdown-item>
                  <el-dropdown-item divided command="logout">
                    <el-icon><SwitchButton /></el-icon>退出登录
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
          <template v-else>
            <router-link to="/login" class="auth-link login-link">登录</router-link>
            <router-link to="/register" class="auth-link register-link">注册</router-link>
          </template>
         <!-- <el-button size="small" round class="admin-btn" @click="goToAdmin">
            <el-icon><Setting /></el-icon>
            管理后台
          </el-button>-->
        </div>
      </div>
    </header>

    <!-- 主内容区 -->
    <main class="main">
      <router-view />
    </main>

    <!-- 底部 -->
    <footer class="footer">
      <div class="footer-inner">
        <div class="footer-top">
          <div class="footer-brand">
            <div class="footer-logo">
              <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
                <rect width="32" height="32" rx="8" fill="url(#footer-logo-gradient)"/>
                <path d="M8 16L14 10L20 16L14 22Z" fill="white" opacity="0.9"/>
                <path d="M14 16L20 10L26 16L20 22Z" fill="white" opacity="0.6"/>
                <defs><linearGradient id="footer-logo-gradient" x1="0" y1="0" x2="32" y2="32"><stop stop-color="#1890ff"/><stop offset="1" stop-color="#36cfc9"/></linearGradient></defs>
              </svg>
              <span>科普作品征集系统</span>
            </div>
            <p class="footer-desc">汇聚科普智慧，传播科学精神</p>
          </div>
          <div class="footer-links">
            <div class="footer-col">
              <h4>快速导航</h4>
              <router-link to="/activity">征集活动</router-link>
              <router-link to="/gallery">作品展示</router-link>
            </div>
            <div class="footer-col">
              <h4>个人服务</h4>
              <router-link to="/user/center">个人中心</router-link>
              <router-link to="/user/works">我的作品</router-link>
            </div>
          </div>
        </div>
        <div class="footer-bottom">
          <p>Copyright &copy; 2026 科普中国资源平台</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  Flag, PictureFilled, ArrowDown, User, Document,
  SwitchButton, Setting
} from '@element-plus/icons-vue'

const router = useRouter()
const isScrolled = ref(false)

// 使用响应式变量存储登录状态
const loginState = ref({
  isLoggedIn: false,
  userName: '用户'
})

// 初始化登录状态
const initLoginState = () => {
  loginState.value.isLoggedIn = !!localStorage.getItem('c_token')
  loginState.value.userName = localStorage.getItem('c_user_nickname') || localStorage.getItem('c_user_phone') || '用户'
}

// 滚动监听
const handleScroll = () => {
  isScrolled.value = window.scrollY > 10
}

// 组件挂载时初始化
onMounted(() => {
  initLoginState()
  window.addEventListener('scroll', handleScroll)

  // 监听storage变化（跨标签页同步）
  window.addEventListener('storage', (e) => {
    if (e.key === 'c_token') {
      initLoginState()
    }
  })

  // 监听登录状态变化事件
  window.addEventListener('loginStateChange', () => {
    initLoginState()
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})

const isLoggedIn = computed(() => loginState.value.isLoggedIn)
const userName = computed(() => loginState.value.userName)

const goHome = () => {
  router.push('/activity')
}

const goToAdmin = () => {
  window.open('http://localhost:8080', '_blank')
}

const handleUserCommand = (command) => {
  if (command === 'center') {
    router.push('/user/center')
  } else if (command === 'works') {
    router.push('/user/works')
  } else if (command === 'logout') {
    handleLogout()
  }
}

const handleLogout = () => {
  localStorage.removeItem('c_token')
  localStorage.removeItem('c_user_id')
  localStorage.removeItem('c_user_phone')
  localStorage.removeItem('c_user_nickname')

  initLoginState()

  ElMessage.success('已退出登录')
  router.push('/activity')
}
</script>

<style scoped lang="scss">
$primary: #1890ff;
$primary-dark: #096dd9;
$text-primary: #1f2937;
$text-secondary: #6b7280;
$text-muted: #9ca3af;
$radius-md: 12px;

.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

// Header
.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid transparent;
  position: sticky;
  top: 0;
  z-index: 100;
  transition: all 0.3s ease;

  &.scrolled {
    border-bottom-color: #f0f0f0;
    box-shadow: 0 1px 12px rgba(0, 0, 0, 0.06);
  }
}

.header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.8;
  }
}

.logo-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
}

.logo-text {
  font-size: 18px;
  font-weight: 700;
  background: linear-gradient(135deg, $primary, #36cfc9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 1px;
}

.nav {
  display: flex;
  gap: 8px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  color: $text-secondary;
  text-decoration: none;
  padding: 8px 18px;
  border-radius: 8px;
  transition: all 0.25s;

  &:hover {
    color: $primary;
    background: var(--color-bg-table-header);
  }

  &.active {
    color: $primary;
    background: #e6f7ff;
    font-weight: 600;
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

// Auth links
.auth-link {
  font-size: 14px;
  font-weight: 500;
  padding: 7px 20px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.25s;
}

.login-link {
  color: $primary;
  border: 1px solid $primary;

  &:hover {
    background: #e6f7ff;
  }
}

.register-link {
  color: #fff;
  background: $primary;
  border: 1px solid $primary;

  &:hover {
    background: $primary-dark;
  }
}

// User dropdown
.user-avatar-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 12px 4px 4px;
  border-radius: 24px;
  transition: background 0.25s;

  &:hover {
    background: #f5f5f5;
  }
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, $primary, #36cfc9);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-name {
  font-size: 14px;
  color: $text-primary;
  font-weight: 500;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.arrow-down {
  font-size: 12px;
  color: $text-muted;
}

.admin-btn {
  border-color: #e5e7eb;
  color: $text-secondary;
  background: #fafafa;

  &:hover {
    color: $primary;
    border-color: $primary;
    background: #e6f7ff;
  }
}

// Main
.main {
  flex: 1;
  background: #f5f7fa;
}

// Footer
.footer {
  background: linear-gradient(180deg, #0f172a, var(--color-text-primary));
  color: #fff;
}

.footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.footer-top {
  display: flex;
  justify-content: space-between;
  padding: 48px 0 32px;
  gap: 40px;
}

.footer-brand {
  max-width: 320px;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
}

.footer-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.6;
  margin: 0;
}

.footer-links {
  display: flex;
  gap: 60px;
}

.footer-col {
  h4 {
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    margin: 0 0 16px;
  }

  a {
    display: block;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.45);
    text-decoration: none;
    margin-bottom: 10px;
    transition: color 0.25s;

    &:hover {
      color: rgba(255, 255, 255, 0.85);
    }
  }
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 20px 0;

  p {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.3);
    margin: 0;
    text-align: center;
  }
}

// Responsive
@media (max-width: 768px) {
  .header-inner {
    height: 56px;
  }

  .logo-text {
    display: none;
  }

  .nav-item {
    padding: 6px 12px;
    font-size: 14px;

    span {
      display: none;
    }
  }

  .footer-top {
    flex-direction: column;
    gap: 32px;
    padding: 32px 0 24px;
  }

  .footer-links {
    gap: 40px;
  }

  .admin-btn span {
    display: none;
  }
}
</style>
